import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logout02Icon, UserStatusIcon } from 'hugeicons-react';
import { useAuth } from '../../contexts/AuthContext';
import '../../Styles/Sidebar/SidebarFooter.css';
import { UserAvatar } from '../Layout/MiniDisplays/UserAvatar';
import { useWindow } from '../../contexts/WindowContext';
import { useSidebar } from '../../contexts/SideBarContext';
import { useChatContext } from '../../contexts/AppDataContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRow,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface SidebarFooterProps {
  onClick?: () => void;
}

const SidebarFooter: React.FC<SidebarFooterProps> = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { setMessages, setShowSalesModal } = useChatContext();
  const { windowWidth, logoImage } = useWindow();
  const { setIsSidebarOpen } = useSidebar();

  const handleHelpClick = useCallback((): void => {
    window.open('https://cognora.ca/faq', '_blank');
  }, []);

  const handleLogoutClick = useCallback((): void => {
    auth.logout();
    navigate('/');
    setMessages([]);
  }, [auth, navigate, setMessages]);

  const handleSettingsClick = useCallback((): void => {
    if(windowWidth < 600){
      setIsSidebarOpen(false);
    }
    navigate('/settings/profile');
  }, [navigate, windowWidth, setIsSidebarOpen]);

  const handleUpgradeClick = useCallback((): void => {
    setShowSalesModal(true);
    if(windowWidth < 600){
      setIsSidebarOpen(false);
    }
  }, [windowWidth, setIsSidebarOpen, setShowSalesModal]);

  const handleTOSClick = useCallback((): void => {
    window.open('https://cognora.ca/terms-of-service', '_blank');
    if(windowWidth < 600){
      setIsSidebarOpen(false);
    }
  }, [windowWidth, setIsSidebarOpen]);

  if (!auth.user) {
    return null;
  }

  return (
    <div className='sidebar-footer'>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className='sidebar-user-item'
            title="Account menu"
          >
            <div className='sidebar-user-item-avatar'>
              <UserAvatar />
            </div>
            <div className='sidebar-user-item-details'>
              <div className='sidebar-user-item-name'>
                {auth?.user?.name ? auth?.user?.name : 'Set your name'}
              </div>
              <div className='sidebar-user-item-plan' aria-label="Subscription plan">
                {auth.subscription?.plan 
                  ? auth.subscription.plan.charAt(0).toUpperCase() + auth.subscription.plan.slice(1).toLowerCase() 
                  : 'Free'}
              </div>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" side="top" className="min-w-[250px]">
          <DropdownMenuRow
            icon={<UserStatusIcon />}
            text={auth.user.email}
            action={handleSettingsClick}
          />

          <DropdownMenuRow text="Settings" action={handleSettingsClick} />

          {!auth.isSubscriptionLoading && auth.subscription?.plan !== 'ultimate' && (
            <DropdownMenuRow
              icon={<img src={logoImage} alt="" />}
              text="Upgrade Plan"
              action={handleUpgradeClick}
            />
          )}

          <DropdownMenuRow text="Help" action={handleHelpClick} />

          <DropdownMenuRow text="Terms of Service" action={handleTOSClick} />

          <DropdownMenuSeparator />

          <DropdownMenuRow
            icon={<Logout02Icon />}
            text="Logout"
            action={handleLogoutClick}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default SidebarFooter;
